import { FlexProps, useToast } from "@chakra-ui/react";
import { useMutation, useQuery } from "@tanstack/react-query";
import {
  patientRegularMessageTemplateOptions,
  portalLinkMessageTemplateOptions,
} from "../../../shared/components/SmsSender/sms-sender.consts";
import {
  NewSmsTicketRequestBody,
  PatientSmsSenderProps,
  PhoneNumber,
  PortalLink,
  RegularMessage,
  SendPortalLinkSmsBody,
  SendSmsBody,
} from "../../../shared/components/SmsSender/sms-sender.types";
import SmsSender from "../../../shared/components/SmsSender/SmsSender";
import useApi from "../../../shared/hooks/useApi";
import { formatErrorResponse } from "../../../shared/utils/format-response-error";
import { phoneFormatter } from "../../../shared/utils/phone-formatter";

const patientMessageTemplateOptions: (RegularMessage | PortalLink)[] = [
  ...Object.values(portalLinkMessageTemplateOptions),
  ...Object.values(patientRegularMessageTemplateOptions),
].sort((a, b) => a.label.localeCompare(b.label));

type Props = Omit<PatientSmsSenderProps, "entity"> & {
  flexProps?: FlexProps;
  onClose: () => void;
};

function PatientSmsSender(props: Props) {
  const { api, queries } = useApi();
  const toast = useToast();

  const patientQuery = useQuery({
    ...queries.patient.get(props.patientId),
    select: (patient) => {
      const patientName = `${patient.firstName} ${patient.lastName}`;
      const patientPhoneNumbers: PhoneNumber[] = patient.phoneNumbers
        .filter((x) => x.isSmsable)
        .map((x) => ({
          label: `${patientName} [ ${phoneFormatter.formatNationalIfValid(x.phonenumber)} ]`,
          value: x.phonenumber,
        }));
      const contactPhonenumber: PhoneNumber[] =
        patient.contacts !== null
          ? patient.contacts
              .filter((x) => x.mobilePhoneNumberSmsable)
              .map((x) => {
                const contactName = `${x.firstName} ${x.lastName}`;
                return {
                  label: `${contactName} [ ${phoneFormatter.formatNationalIfValid(
                    x.mobilePhoneNumber
                  )} ]`,
                  value: x.mobilePhoneNumber,
                };
              })
          : [];
      return {
        firstName: patient.firstName,
        phoneNumbers: [...patientPhoneNumbers, ...contactPhonenumber],
        status: patient.status,
      };
    },
  });

  const createTicket = useMutation({
    mutationFn: (newTicketRequest: Pick<NewSmsTicketRequestBody, "phoneNumber" | "message">) => {
      return api.post("./comm_center/sms", {
        body: {
          ...newTicketRequest,
          message: newTicketRequest.message,
          phoneNumber: newTicketRequest.phoneNumber,
          patientId: props.patientId,
          caregiverId: null,
          topic: "Patient",
        },
      });
    },
    onSuccess: () => {
      toast({
        title: "SMS sent successfully",
        status: "success",
        position: "top-right",
      });
      props.onClose();
    },
    onError: (error) => {
      toast({
        title: "Could not create new ticket",
        description: formatErrorResponse(error),
        status: "error",
        position: "top-right",
      });
    },
  });

  const sendPortalLinkSms = useMutation({
    mutationFn: (sendPortalLinkSmsBody: SendPortalLinkSmsBody) => {
      return api.post(
        "/portal/agencies/:agencyId/agency_members/:agencyMemberId/redirect_url/send",
        {
          body: {
            ...sendPortalLinkSmsBody,
            patientId: props.patientId,
          },
        }
      );
    },
    onSuccess: () => {
      toast({
        title: "Portal link SMS sent successfully",
        status: "success",
        position: "top-right",
      });
      props.onClose();
    },
    onError: (error) => {
      toast({
        title: "Could not send portal link SMS",
        description: formatErrorResponse(error),
        status: "error",
        position: "top-right",
      });
    },
  });

  const handleSend = (body: SendSmsBody | SendPortalLinkSmsBody) => {
    return "redirectUrl" in body ? sendPortalLinkSms.mutate(body) : createTicket.mutate(body);
  };

  return (
    <SmsSender
      entityQuery={patientQuery}
      flexProps={props.flexProps}
      isSending={createTicket.isPending || sendPortalLinkSms.isPending}
      messageTemplates={patientMessageTemplateOptions}
      predefinedMessageTemplate={props.predefinedMessageTemplate}
      predefinedPhoneNumber={props.predefinedPhoneNumber}
      onClose={props.onClose}
      onSend={handleSend}
    />
  );
}

export default PatientSmsSender;
